import { CreateEngine, InfraScene, isNull } from "../infra3d";
import { Obj, FeatureHandler, FrameFeatureHandler, LayerHandler, LRHandler, MeasureHandler, STREAMTYPE, MeasureModeHandler, CursorStyleHandler, ToolHandler, UserInputHandler, } from "./internal";
import { StereoGeoFrameCollection } from "./geoframe/stereogeoframecollection";
import { CubemapGeoFrameCollection } from "./geoframe/cubemapgeoframecollection";
import { PerspectiveGeoFrameCollection } from "./geoframe/perspectivegeoframecollection";
import { PanoGeoFrameCollection } from "./geoframe/panogeoframecollection";
import { UserInputWrapper } from "./userinput/userinputwrapper";
import { FrustumHandler } from "./utils/frustumhandler";
import { Infra3DImageProvider } from "./api/Infra3DImageProvider";
import { AuthMode } from "./viewer/AuthMode";
/**
 * @enum the measurement mode, used by toolhandler and tools
 */
export var MEASUREMODE;
(function (MEASUREMODE) {
    MEASUREMODE[MEASUREMODE["MONOPLOTTING"] = 0] = "MONOPLOTTING";
    MEASUREMODE[MEASUREMODE["LEFTRIGHT"] = 1] = "LEFTRIGHT";
    MEASUREMODE[MEASUREMODE["DEFINED_BY_SCENE"] = 2] = "DEFINED_BY_SCENE";
    MEASUREMODE[MEASUREMODE["MULTIVIEW"] = 3] = "MULTIVIEW";
    MEASUREMODE[MEASUREMODE["MONOAPPROXIMATION"] = 4] = "MONOAPPROXIMATION";
    MEASUREMODE[MEASUREMODE["PANODEPTHMAPINTERSECTION"] = 5] = "PANODEPTHMAPINTERSECTION";
    MEASUREMODE[MEASUREMODE["PANODEPTHMAPINTERSECTION_MULTIVIEW_COMBINED"] = 6] = "PANODEPTHMAPINTERSECTION_MULTIVIEW_COMBINED";
    MEASUREMODE[MEASUREMODE["NONE"] = 7] = "NONE";
    MEASUREMODE[MEASUREMODE["PANO_TWO_CLICK"] = 8] = "PANO_TWO_CLICK";
    MEASUREMODE[MEASUREMODE["PANODEPTHMAPINTERSECTION_PANO_TWO_CLICK_COMBINED"] = 9] = "PANODEPTHMAPINTERSECTION_PANO_TWO_CLICK_COMBINED";
})(MEASUREMODE || (MEASUREMODE = {}));
/**
 * @enum the measurement mode, used by toolhandler and tools
 */
export var GEOFRAMETYPE;
(function (GEOFRAMETYPE) {
    GEOFRAMETYPE[GEOFRAMETYPE["PERSPECTIVE_MONO"] = 0] = "PERSPECTIVE_MONO";
    GEOFRAMETYPE[GEOFRAMETYPE["PERSPECTIVE_LEFT_DEPTH"] = 1] = "PERSPECTIVE_LEFT_DEPTH";
    GEOFRAMETYPE[GEOFRAMETYPE["PERSPECTIVE_LEFT_RIGHT_DEPTH"] = 2] = "PERSPECTIVE_LEFT_RIGHT_DEPTH";
    GEOFRAMETYPE[GEOFRAMETYPE["PERSPECTIVE_LEFT_RIGHT"] = 3] = "PERSPECTIVE_LEFT_RIGHT";
    GEOFRAMETYPE[GEOFRAMETYPE["PERSPECTIVE_RIGHT_DEPTH"] = 4] = "PERSPECTIVE_RIGHT_DEPTH";
    GEOFRAMETYPE[GEOFRAMETYPE["PANO_ALL_MONO"] = 5] = "PANO_ALL_MONO";
    GEOFRAMETYPE[GEOFRAMETYPE["PANO_ALL_DEPTH"] = 6] = "PANO_ALL_DEPTH";
    GEOFRAMETYPE[GEOFRAMETYPE["UNKNOWN"] = 7] = "UNKNOWN";
    GEOFRAMETYPE[GEOFRAMETYPE["MULTIPERSPECTIVE_ALL_MONO"] = 8] = "MULTIPERSPECTIVE_ALL_MONO";
    GEOFRAMETYPE[GEOFRAMETYPE["MULTIPERSPECTIVE_ALL_DEPTH"] = 9] = "MULTIPERSPECTIVE_ALL_DEPTH";
})(GEOFRAMETYPE || (GEOFRAMETYPE = {}));
/**
 * @enum the image display mode
 */
export var IMAGEDISPLAYMODE;
(function (IMAGEDISPLAYMODE) {
    IMAGEDISPLAYMODE[IMAGEDISPLAYMODE["PLAYING"] = 0] = "PLAYING";
    IMAGEDISPLAYMODE[IMAGEDISPLAYMODE["FRAMESTEPPING"] = 1] = "FRAMESTEPPING";
})(IMAGEDISPLAYMODE || (IMAGEDISPLAYMODE = {}));
export var PLAYMODE;
(function (PLAYMODE) {
    PLAYMODE[PLAYMODE["ORBIT_PLAY"] = 0] = "ORBIT_PLAY";
    PLAYMODE[PLAYMODE["STREAM_PLAY"] = 1] = "STREAM_PLAY";
})(PLAYMODE || (PLAYMODE = {}));
/**
 * Handles the loading and quering of EdgeNodeNetwork
 * @class Scene
 * @author Benjamin Loesch, based on code by Joel Burkhard ported to typescript Robert Wüest
 * @constructor
 * @extends Obj
 */
export class Scene extends Obj {
    // FIXME: Not pass container for dimension they should be on canvas.
    constructor(container, canvas, canvasContainer, imageProviderOptions, authObject, authMode) {
        super();
        this.currentGeoFrameCollection = null;
        this.engine = null;
        this.cbfGeoFrameCollectionChanged = null;
        this._measurehandler = null;
        this.userInputHandler = null;
        this._measureModeHandler = null;
        this._cursorStyleHandler = null;
        this.playmode = PLAYMODE.STREAM_PLAY;
        this.authmode = AuthMode.view;
        /**
         * True if the image borders in panormama mode should be hidden in shader...
         * @type {boolean}
         */
        this._hidePanoImageBorders = true;
        this._frustumHandler = null;
        /**
         * True to disable the logging of downloaded tiles and meta-data
         * @type {boolean}
         */
        this.disableLogging = false;
        /**
         * True to enable Least-Square-Matching in Left/Right Measure Mode
         * @type(boolean)
         */
        this.lsmenabled = false;
        this.lsmprops = {
            templateWidth: 50,
            templateHeight: 50,
            searchWindowWidth: 200,
            NCC_Threshold: 0.75,
            adjustRadiometry: false,
        };
        this.multiviewprops = { displayEstimationPoint: true, displayEpipolarLine: true };
        /**
         * Jump-Threshold in m to reset the panorama orientation with respect to the driving direction.
         * @type {number}
         * @default 50
         */
        this.panoramaresetthreshold = 50;
        /**
         * Set how far away overlayed objects are being drawn.
         * @param {number} distance
         */
        this.setRenderDistance = function (distance) {
            if (this._frustumHandler) {
                this._frustumHandler.setRenderDistance(distance);
            }
        };
        actualscene = this;
        this._toolHandler = new ToolHandler(this);
        this._LRHandler = new LRHandler();
        this._LayerHandler = new LayerHandler(this);
        this.imageProvider = new Infra3DImageProvider(imageProviderOptions, authObject);
        this._imageProviderOptions = imageProviderOptions;
        this.engine = CreateEngine("THREEJS", {
            canvasElement: canvas,
            width: container.clientWidth,
            height: container.clientHeight,
            containerElement: container,
            imageProvider: this.imageProvider,
        });
        this.infrascene = new InfraScene(this.engine);
        this.userInputHandler = new UserInputHandler(this);
        this.userInputHandler.initSceneEvents();
        this._userinputapi = new UserInputWrapper(this.userInputHandler);
        this._featureHandler = new FeatureHandler(this);
        this._frameFeatureHandler = new FrameFeatureHandler(this);
        this._measurehandler = new MeasureHandler(this);
        this._measureModeHandler = new MeasureModeHandler(this);
        this._cursorStyleHandler = new CursorStyleHandler(this);
        this._frustumHandler = new FrustumHandler(this, 6, 0.01);
        this._frustumHandler.activateSlicer();
        this._authObject = authObject;
        this.authmode = authMode;
    }
    setGeoFrames(geoFrame, geoFrames, cachingStepsize = 1) {
        if (this.currentGeoFrameCollection === null) {
            this._createGeoFrameCollection(geoFrame, geoFrames, cachingStepsize);
        }
        else if (this.currentGeoFrameCollection.currentgeoframe.stream !== geoFrame.stream ||
            this.currentGeoFrameCollection.currentgeoframe.collection !== geoFrame.collection) {
            this._createGeoFrameCollection(geoFrame, geoFrames, cachingStepsize);
        }
        else {
            this.currentGeoFrameCollection.searchToFramenumber(this.currentGeoFrameCollection.frameIndex(geoFrame), cachingStepsize);
        }
    }
    _createGeoFrameCollection(currentgeoframe, geoframearray, cachingStepsize) {
        let geoFrameCollection;
        const streamtype = currentgeoframe.getMeasureInfo().streamtype;
        switch (streamtype) {
            case STREAMTYPE.MONO:
                geoFrameCollection = new PerspectiveGeoFrameCollection(this, currentgeoframe, geoframearray);
                break;
            case STREAMTYPE.STEREO:
                geoFrameCollection = new StereoGeoFrameCollection(this, currentgeoframe, geoframearray);
                break;
            case STREAMTYPE.CALOTTE:
                geoFrameCollection = new PanoGeoFrameCollection(this, currentgeoframe, geoframearray);
                break;
            case STREAMTYPE.CUBEMAP:
                geoFrameCollection = new CubemapGeoFrameCollection(this, currentgeoframe, geoframearray);
                break;
        }
        geoFrameCollection.setParentGeoFrameCollection(currentgeoframe, geoframearray);
        this._setGeoFrameCollection(geoFrameCollection, cachingStepsize);
    }
    // FIXME: refactor move if's to collection, make if-less!
    //        Refactor this code is hard to understand, ups I mean especially. All code is.
    //        Especially the usage of currentGeoframeCollection and geoframCollection.
    _setGeoFrameCollection(geoFrameCollection, cachingStepsize) {
        if (this.currentGeoFrameCollection !== null) {
            geoFrameCollection.align_panoramic_view(this.currentGeoFrameCollection.currentgeoframe, geoFrameCollection.currentgeoframe, this.navigator.needsResetPanoOrientation);
            this.navigator.needsResetPanoOrientation = false;
        }
        // FIXME / REVIEW
        if (this.currentGeoFrameCollection !== null) {
            this.currentGeoFrameCollection.unload();
        }
        this.engine.crossorigin = geoFrameCollection.crossorigin;
        this.currentGeoFrameCollection = geoFrameCollection;
        this.currentGeoFrameCollection.load(this.currentGeoFrameCollection.currentgeoframe, cachingStepsize);
        if (geoFrameCollection.type == STREAMTYPE.CALOTTE && !this._hidePanoImageBorders) {
            geoFrameCollection.smearOff();
        }
        // FIXME: Use .centroid
        this.engine.setCentroid([
            this.currentGeoFrameCollection.currentgeoframe.easting,
            this.currentGeoFrameCollection.currentgeoframe.northing,
            this.currentGeoFrameCollection.currentgeoframe.height,
        ]);
        if (this.cbfGeoFrameCollectionChanged !== null) {
            this.cbfGeoFrameCollectionChanged(this.currentGeoFrameCollection);
        }
        this.resize();
    }
    getCurrentGeoFrameCollection() {
        return this.currentGeoFrameCollection;
    }
    /**
     * Returns the current GeoFrame.
     * @method getEdgeNodeNetwork
     * @return {Stream} the current stream
     */
    getCurrentGeoframe() {
        if (this.getCurrentGeoFrameCollection())
            return this.getCurrentGeoFrameCollection().getCurrentGeoFrame();
        return null;
    }
    /**
     * Returns the featurehandler
     * @method getFeatureHandler
     * @return {FeatureHandler} returns the FeatureHandler.
     */
    getFeatureHandler() {
        return this._featureHandler;
    }
    getFrameFeatureHandler() {
        return this._frameFeatureHandler;
    }
    /**
     * Returns the ToolHandler
     * @return {ToolHandler} returns the Tool Handler
     */
    getToolHandler() {
        return this._toolHandler;
    }
    /**
     * Returns the MeasureHandler
     * @return {MeasureHandler} returns the Measure-Handler
     */
    getMeasureHandler() {
        return this._measurehandler;
    }
    getMeasureModeHandler() {
        return this._measureModeHandler;
    }
    getCursorStyleHandler() {
        return this._cursorStyleHandler;
    }
    /**
     * Returns the LRHanlder
     *
     * @return {LRHandler}
     */
    getLRHandler() {
        return this._LRHandler;
    }
    /**
     * Returns the LayerHanlder
     *
     * @return {LayerHandler}
     */
    getLayerHandler() {
        return this._LayerHandler;
    }
    /**
     * Returns the Frustum Handler
     * @returns {FrustumHandler}
     */
    getFrustumHandler() {
        return this._frustumHandler;
    }
    /**
     * Sets the OnGeoFrameCollectionChanged-Callback-Function. This function should only be used by the
     * UserInputHandler, when subscribing this event.
     *
     * @param {function(geoFrameCollection)} f
     */
    setOnGeoFrameCollectionChanged(f) {
        this.cbfGeoFrameCollectionChanged = f;
    }
    resize(width, height) {
        if (!width)
            width = parseInt(this.engine.getCanvas().getAttribute("width"));
        if (!height)
            height = parseInt(this.engine.getCanvas().getAttribute("height"));
        this.engine.resize(width, height);
        // Needs to be done, becuase otherwise the features are having a thicker line #WOODOO
        // Only shows up when loading the scene in a very small window and then making it much larger.
        this._featureHandler.featurecollections.forEach((featurecollection) => {
            featurecollection.update();
        });
        this.infrascene.userInputHandler.onResize({ newwidth: width, newheight: height });
        this.infrascene.render();
    }
    /**
     * @method set the gamma value for image-tiles
     * @param {number} gammavalue [0.2 ... 3]
     */
    setGamma(gammavalue) {
        this.infrascene.setGamma(gammavalue);
        this.infrascene.render();
    }
    getGamma() {
        return this.infrascene.getGamma();
    }
    hidePanoImageBorders() {
        this._hidePanoImageBorders = true;
        const geoframecollection = this.getCurrentGeoFrameCollection();
        if (!isNull(geoframecollection) && geoframecollection.type == STREAMTYPE.CALOTTE) {
            geoframecollection.smearOn();
        }
    }
    /**
     * @method shows the pano image borders -> due to the light condition during the image capturing images broders are visible
     */
    showPanoImageBorders() {
        this._hidePanoImageBorders = false;
        const geoframecollection = this.getCurrentGeoFrameCollection();
        if (!isNull(geoframecollection) && geoframecollection.type == STREAMTYPE.CALOTTE) {
            geoframecollection.smearOff();
        }
    }
    /**
     * Set the Jump-Threshold in m to reset the panorama orientation with respect to the driving direction.
     * @param threshold
     */
    setPanoResetThreshold(threshold) {
        this.panoramaresetthreshold = threshold;
    }
    /**
     * @method activates Least-Square-Matching
     */
    enableLSMforLeftRightMeasurements() {
        this.lsmenabled = true;
    }
    /**
     * @method deactivate Least-Square-Matching
     */
    disableLSMforLeftRightMeasurements() {
        this.lsmenabled = false;
    }
    /**
     * @method set multi view measurement properties
     */
    setMultiViewProperties(props) {
        this.multiviewprops = props;
    }
    /**
     * @method get multi view measurement properties
     */
    getMultiViewProperties() {
        return this.multiviewprops;
    }
    getLSMProperties() {
        return this.lsmprops;
    }
    setLSMProperties(props) {
        this.lsmprops = props;
    }
    getMeasureMode() {
        return this._measureModeHandler.getMeasureMode();
    }
    getPlayMode() {
        return this.playmode;
    }
    add(object) {
        this.infrascene.add(object);
    }
    remove(object) {
        this.infrascene.remove(object);
    }
    setNavigator(navigator) {
        this.navigator = navigator;
    }
    setViewer(viewer) {
        this.viewer = viewer;
    }
    destroy() {
        this._toolHandler = null;
        this._LRHandler = null;
        this._LayerHandler = null;
        this.engine.destroy();
        this.engine = null;
        this.infrascene.destroy();
        this.userInputHandler = null;
        this._featureHandler = null;
        this._frameFeatureHandler = null;
        this._measurehandler = null;
        this._measureModeHandler = null;
        this._cursorStyleHandler = null;
    }
    destroySubScene(subscene) {
        subscene._toolHandler = null;
        subscene._LRHandler = null;
        subscene._LayerHandler = null;
        subscene.engine.destroy(false);
        subscene.engine = null;
        subscene.infrascene.destroy();
        subscene.userInputHandler = null;
        subscene._featureHandler = null;
        subscene._frameFeatureHandler = null;
        subscene._measurehandler = null;
        subscene._measureModeHandler = null;
        subscene._cursorStyleHandler = null;
        subscene = null;
    }
    createSubScene(divid) {
        const div = document.getElementById(divid);
        if (!div) {
            console.log("div for subscene needs to be added to the DOM!");
            return;
        }
        const canvas = document.createElement("canvas");
        canvas.id = "infra3dsdk-subscene-canvas";
        canvas.style.position = "absolute";
        canvas.setAttribute("tabindex", "0");
        const subscene = new Scene(div, canvas, div, this._imageProviderOptions, this._authObject, this.authmode);
        actualscene = this; //because this is overwritten by constructor...
        //exchange cache
        subscene.infrascene.engine.dispMapCache = this.infrascene.engine.dispMapCache;
        subscene.infrascene.engine.imageCache = this.infrascene.engine.imageCache;
        subscene.infrascene.engine.textureCache = this.infrascene.engine.textureCache;
        subscene.infrascene.engine.dispMapCache = this.infrascene.engine.dispMapCache;
        this.infrascene.engine.symbolCache = this.infrascene.engine.symbolCache;
        this.infrascene.engine.headerCache = this.infrascene.engine.headerCache;
        subscene.disableLogging = true;
        //abort dragging on mouseleave
        const canceldragging = (e) => {
            if (e.buttons)
                subscene.infrascene.userInputHandler.onPointerUp(e);
        };
        div.onmouseleave = (e) => {
            canceldragging(e);
        };
        return subscene;
    }
    logStats() {
        console.log("Items in textureCache: " + actualscene.engine.textureCache._count);
        console.log("Items in dispMapCache: " + actualscene.engine.dispMapCache._count);
        console.log("Items in imageCache: " + actualscene.engine.imageCache._count);
        console.log("Textures in renderer: " + actualscene.engine.renderer.info.memory.textures);
        console.log("Geometries in renderer: " + actualscene.engine.renderer.info.memory.geometries);
        console.log("Webgl Programs in renderer: " + actualscene.engine.renderer.info.programs.length);
    }
    get userinputapi() {
        return this._userinputapi;
    }
    setAuthObject(authObject) {
        this._authObject = authObject;
        this.imageProvider.authObject = authObject;
    }
}
export let actualscene = null;
//# sourceMappingURL=scene.js.map